import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Button, Card } from '../../components/shared/UI';
import API from '../../utils/apiClient';

export default function ChangePasswordPage() {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({ currentPassword:'', newPassword:'', confirmPassword:'' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      addToast('All fields are required.','error'); return;
    }
    if (form.newPassword.length < 6) {
      addToast('New password must be at least 6 characters.','error'); return;
    }
    if (form.newPassword !== form.confirmPassword) {
      addToast('New passwords do not match.','error'); return;
    }
    if (form.newPassword === form.currentPassword) {
      addToast('New password must be different from current password.','error'); return;
    }

    setLoading(true);
    try {
      await API.post('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword:     form.newPassword,
      });

      // Clear temp_password flag in context
      updateUser({ temp_password: 0 });
      addToast('Password changed successfully!','success');

      // Redirect based on role
      const role = user?.role;
      if (role === 'super_admin')             navigate('/super-admin/dashboard');
      else if (['teacher','admin'].includes(role)) navigate('/admin/dashboard');
      else navigate('/student/dashboard');
    } catch (err) {
      addToast(err.message || 'Failed to change password.','error');
    } finally {
      setLoading(false);
    }
  };

  const isForced = user?.temp_password === 1 || user?.temp_password === true;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gold-100 border-2 border-gold-400 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔐</span>
          </div>
          <h1 className="text-2xl font-bold text-de-black mb-2">
            {isForced ? 'Change Your Password' : 'Update Password'}
          </h1>
          {isForced ? (
            <div className="px-4 py-3 bg-gold-50 border border-gold-400 rounded-xl">
              <p className="text-sm text-de-black font-semibold">⚠ Temporary password detected</p>
              <p className="text-xs text-gray-600 mt-1">
                Your account was created with a temporary password. You must set a new password before continuing.
              </p>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Enter your current password and choose a new one.</p>
          )}
        </div>

        <Card className="p-6" accent="red">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-de-black mb-1.5">
                {isForced ? 'Temporary Password' : 'Current Password'}
              </label>
              <input
                type="password"
                value={form.currentPassword}
                onChange={e => set('currentPassword', e.target.value)}
                placeholder={isForced ? 'Enter: DeutschBridge@2026' : 'Your current password'}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-surface focus:border-brand-500 focus:bg-white focus:outline-none text-sm transition-all"
              />
              {isForced && (
                <p className="text-xs text-gray-400 mt-1">The temporary password was sent to your email.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-de-black mb-1.5">New Password</label>
              <input
                type="password"
                value={form.newPassword}
                onChange={e => set('newPassword', e.target.value)}
                placeholder="At least 6 characters"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-surface focus:border-brand-500 focus:bg-white focus:outline-none text-sm transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-de-black mb-1.5">Confirm New Password</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)}
                placeholder="Repeat new password"
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-surface focus:border-brand-500 focus:bg-white focus:outline-none text-sm transition-all"
              />
              {form.confirmPassword && form.newPassword !== form.confirmPassword && (
                <p className="text-xs text-brand-500 mt-1 font-medium">Passwords do not match.</p>
              )}
              {form.confirmPassword && form.newPassword === form.confirmPassword && form.newPassword.length >= 6 && (
                <p className="text-xs text-green-600 mt-1 font-medium">✓ Passwords match.</p>
              )}
            </div>

            <div className="pt-2">
              <Button type="submit" variant="primary" size="lg" loading={loading} className="w-full">
                {isForced ? 'Set New Password & Continue' : 'Change Password'}
              </Button>
            </div>
          </form>
        </Card>

        <p className="text-center text-xs text-gray-400 mt-4">
          DeutschBridge Institute · 0202071417
        </p>
      </div>
    </div>
  );
}
